import { useCallback, useEffect, useRef, useState } from 'react';

export type UseMarqueeAnimationParams = {
  speed?: number;
};

export function useMarqueeAnimation(config: UseMarqueeAnimationParams = {}) {
  const { speed = 1 } = config;

  const xRef = useRef(0);
  const [x, setX] = useState(0);
  const animationRef = useRef<number | null>(null);
  const contentWidthRef = useRef<number>(0);

  const dragStartX = useRef<number | null>(null);
  const isDragging = useRef(false);

  const loopX = (current: number) => {
    const contentWidth = contentWidthRef.current;
    if (contentWidth === 0) return current;

    if (current <= -contentWidth) {
      return current + contentWidth;
    } else if (current >= 0) {
      return current - contentWidth;
    }
    return current;
  };

  const step = useCallback(() => {
    if (!isDragging.current) {
      xRef.current -= speed;
      xRef.current = loopX(xRef.current);
      setX(xRef.current);
    }
    animationRef.current = requestAnimationFrame(step);
  }, [speed]);

  const playMarquee = () => {
    if (!animationRef.current) {
      animationRef.current = requestAnimationFrame(step);
    }
  };

  const pauseMarquee = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  };

  const prepareDrag = (clientX: number) => {
    isDragging.current = true;
    dragStartX.current = clientX;
    pauseMarquee();
  };

  const updateDrag = (clientX: number) => {
    if (!isDragging.current || dragStartX.current == null) return;
    const delta = clientX - dragStartX.current;
    dragStartX.current = clientX;
    xRef.current += delta;
    xRef.current = loopX(xRef.current);
    setX(xRef.current);
  };

  const endDrag = () => {
    isDragging.current = false;
    dragStartX.current = null;
    playMarquee();
  };

  const setContentWidth = (width: number) => {
    contentWidthRef.current = width;
  };

  useEffect(() => {
    playMarquee();
    return () => pauseMarquee();
  }, [step]);

  return {
    x,
    playMarquee,
    pauseMarquee,
    prepareDrag,
    updateDrag,
    endDrag,
    setContentWidth,
  };
}
